import { useMemo } from 'react';
import { FormatterType } from '@fabric-ui/components';
import { useCustomSearchParams } from '@hooks/UseCustomSearchParams2';
import useQueryObject from '@hooks/useQueryObjectSql';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnFiltersState } from '@tanstack/react-table';
import { and, field, or } from '@utils/filter';
import { filterToExpression, isEmptyFilter, isEmptyObject } from '@utils/filterUtils';
import { deserializeSortItem } from '@utils/utils';
import { DwQueryRequest, SortDir } from '@views/CustomDashboards/types';
import { FieldType, FieldTypeEnum, PREVIEW_PAGE_SIZE } from '../types';
import { generateNestedSqlAccessor } from '../utils';

const convertFormatterTypeToFieldTypeEnum = (formatterType: string): string => {
  const mapping = {
    [FormatterType.number]: FieldTypeEnum[FieldType.Number],
    [FormatterType.string]: FieldTypeEnum[FieldType.Text],
    [FormatterType.boolean]: FieldTypeEnum[FieldType.Boolean],
    [FormatterType.date]: FieldTypeEnum[FieldType.Date],
    [FormatterType.historicDate]: FieldTypeEnum[FieldType.DateTime],
    [FormatterType.array]: FieldTypeEnum[FieldType.Text],
    [FormatterType.json]: FieldTypeEnum[FieldType.Text],
    [FormatterType.percentage]: FieldTypeEnum[FieldType.Number],
  };
  return mapping[formatterType] || FieldTypeEnum[FieldType.Text];
};

const formatterToTypeResolver = {
  [FormatterType.number]: 'Number',
  [FormatterType.boolean]: 'BOOLEAN',
  [FormatterType.array]: 'Array',
  [FormatterType.json]: 'Object',
  [FormatterType.string]: 'Text',
};

export const useMappingPreviewData = ({
  page,
  workflowId,
  enabled,
  instanceId,
  columnFilters = [],
  fieldTypesMap = {},
}: {
  page: number;
  workflowId: string | undefined;
  enabled?: boolean;
  instanceId?: string;
  columnFilters?: ColumnFiltersState;
  fieldTypesMap: Record<string, string>;
}) => {
  const [filters] = useCustomSearchParams({});
  const orderBy = deserializeSortItem(filters?.orderBy?.[0] ? `rawlogs.DOCUMENT.${filters?.orderBy?.[0]}` : 'rawlogs.AVALOR_ID__DESC');
  const queryEnabled = enabled && !!workflowId;

  const sourceFilter = useMemo(
    () =>
      instanceId
        ? field('rawlogs.dsi_id').str.equals(instanceId)
        : or(field('rawlogs.dsi_id').str.equals(''), field('rawlogs.dsi_id').str.empty()),
    [instanceId]
  );

  const workflowFilter = useMemo(() => field("AVALOR_CONTEXT(rawlogs, 'workflowId')").str.equals(workflowId as string), [workflowId]);

  const columnFiltersObject = useMemo(() => Object.fromEntries(columnFilters.map(f => [f.id, f.value])), [columnFilters]);

  const convertedFieldTypesMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldTypesMap).map(([key, formatterType]) => [
          generateNestedSqlAccessor(key, 'rawlogs.DOCUMENT', formatterType.toLowerCase() as FormatterType),
          convertFormatterTypeToFieldTypeEnum(formatterType),
        ])
      ),
    [fieldTypesMap]
  );

  const whereFilter = useMemo(
    () =>
      filterToExpression(
        convertedFieldTypesMap,
        Object.fromEntries(
          Object.entries({ ...filters, ...columnFiltersObject }).map(([key, value]) => [
            generateNestedSqlAccessor(key, 'rawlogs.DOCUMENT', formatterToTypeResolver[fieldTypesMap[key]] as FormatterType),
            value,
          ])
        )
      ),
    [filters, columnFiltersObject, convertedFieldTypesMap, fieldTypesMap]
  );
  const allFilters = useMemo(
    () => [sourceFilter, workflowFilter, ...(!isEmptyObject(whereFilter) && !isEmptyFilter(whereFilter) ? [whereFilter] : [])],
    [sourceFilter, workflowFilter, whereFilter]
  );

  const dataQuery: DwQueryRequest = {
    select: {
      dims: [{ name: 'rawlogs.DOCUMENT' }, { name: orderBy.property }],
      metrics: [],
    },
    filter: and(...allFilters),
    sorting: [
      {
        name: orderBy.property,
        dir: orderBy.isAsc ? SortDir.ASC : SortDir.DESC,
      },
    ],
    top: {
      size: PREVIEW_PAGE_SIZE,
      offset: (page || 0) * PREVIEW_PAGE_SIZE,
      groupOthers: false,
    },
    projectionId: {
      name: 'rawlogs',
      builtIn: true,
    },
    distinct: false,
  };

  const { data, totalRowCount, isLoading, isRefetching } = useQueryObject({
    queryObject: dataQuery,
    totalRowCount: true,
    options: { enabled: queryEnabled, placeholderData: keepPreviousData },
    onSuccess: data => data.map(d => d['rawlogs.DOCUMENT']),
  });
  return useMemo(
    () => ({
      data,
      totalRowCount,
      isLoading,
      isRefetching,
    }),
    [data, totalRowCount, isLoading, isRefetching]
  );
};
